import React, { useState, useRef, useCallback, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import { uploadDocument, DOCUMENT_CATEGORIES, ACCESS_LEVELS } from '../../services/docuvaultService';
import { checkApiHealth } from '../../services/s3UploadService';

const {
  FiX, FiUploadCloud, FiShield, FiPlus, FiTrash2, FiCheck,
  FiAlertTriangle, FiFile, FiWifiOff, FiRefreshCw, FiLock,
} = FiIcons;

// ── Upload phase definitions (ordered) ───────────────────────────────────────
const PHASES = [
  { id: 'checksum', label: 'Computing SHA-256 checksum…',  baseStart: 0,  baseEnd: 8  },
  { id: 'presign',  label: 'Requesting secure S3 URL…',    baseStart: 8,  baseEnd: 18 },
  { id: 'upload',   label: 'Uploading directly to S3…',    baseStart: 18, baseEnd: 88 },
  { id: 'metadata', label: 'Saving document record…',      baseStart: 88, baseEnd: 97 },
  { id: 'done',     label: 'Upload complete!',              baseStart: 97, baseEnd: 100 },
];

const ACCEPTED = '.pdf,.doc,.docx,.jpg,.jpeg,.png,.txt,.xls,.xlsx';
const MAX_MB   = 50;

function formatBytes(bytes) {
  if (!bytes) return '0 B';
  const u = ['B', 'KB', 'MB', 'GB'];
  let i = 0, s = bytes;
  while (s >= 1024 && i < u.length - 1) { s /= 1024; i++; }
  return `${s.toFixed(i > 0 ? 1 : 0)} ${u[i]}`;
}

/** Convert phase + s3Progress (0–100) to a single overall percentage */
function computeOverallPct(phase, s3Progress) {
  const p = PHASES.find(x => x.id === phase);
  if (!p) return 0;
  if (phase === 'done') return 100;
  if (phase === 'upload') {
    return Math.round(p.baseStart + (s3Progress / 100) * (p.baseEnd - p.baseStart));
  }
  return p.baseEnd;
}

const defaultMeta = {
  category: 'general', description: '', tags: '', accessLevel: 'private',
  authorizedParties: [], authorizationRequired: false,
  authorizationThreshold: 1, expiryDate: '',
};

// ── ApiStatusBanner ───────────────────────────────────────────────────────────
function ApiStatusBanner({ status }) {
  if (status === 'ok') return null;
  return (
    <motion.div
      initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }}
      className="flex items-center gap-2 px-4 py-2.5 bg-amber-50 border-b border-amber-200 text-amber-800 text-[10px] font-bold"
    >
      <SafeIcon icon={FiWifiOff} className="w-3.5 h-3.5 flex-shrink-0" />
      S3 API server is offline — uploads will fail until the server is running on port 4000.
    </motion.div>
  );
}

// ── UploadProgressPanel ───────────────────────────────────────────────────────
function UploadProgressPanel({ phase, s3Progress }) {
  const overallPct = computeOverallPct(phase, s3Progress);

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6 py-4">
      {/* Circular progress ring */}
      <div className="flex justify-center">
        <div className="relative w-20 h-20">
          <svg className="w-20 h-20 -rotate-90" viewBox="0 0 80 80">
            <circle cx="40" cy="40" r="34" fill="none" stroke="#e5e7eb" strokeWidth="6" />
            <motion.circle
              cx="40" cy="40" r="34" fill="none" stroke="#B8860B" strokeWidth="6"
              strokeLinecap="round" strokeDasharray={`${2 * Math.PI * 34}`}
              animate={{ strokeDashoffset: 2 * Math.PI * 34 * (1 - overallPct / 100) }}
              transition={{ duration: 0.4, ease: 'easeOut' }}
            />
          </svg>
          <div className="absolute inset-0 flex items-center justify-center">
            {phase === 'done'
              ? <SafeIcon icon={FiCheck} className="w-8 h-8 text-green-500" />
              : <span className="text-sm font-black text-accent-navy">{overallPct}%</span>
            }
          </div>
        </div>
      </div>

      {/* Phase step list */}
      <div className="space-y-2">
        {PHASES.filter(p => p.id !== 'done').map((p, i) => {
          const phaseIdx    = PHASES.findIndex(x => x.id === phase);
          const thisIdx     = PHASES.findIndex(x => x.id === p.id);
          const isActive    = p.id === phase;
          const isCompleted = phaseIdx > thisIdx || phase === 'done';

          return (
            <div
              key={p.id}
              className={`flex items-center gap-3 px-4 py-2.5 rounded-xl transition-all ${
                isActive ? 'bg-accent-navy/5 border border-accent-navy/10' : ''
              }`}
            >
              <div className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 ${
                isCompleted ? 'bg-green-500' : isActive ? 'bg-accent-navy' : 'bg-gray-200'
              }`}>
                {isCompleted
                  ? <SafeIcon icon={FiCheck} className="w-3 h-3 text-white" />
                  : isActive
                    ? <div className="w-2 h-2 bg-white rounded-full animate-pulse" />
                    : <span className="text-[8px] font-black text-gray-400">{i + 1}</span>
                }
              </div>
              <span className={`text-xs font-${isActive ? 'bold' : 'normal'} ${
                isCompleted ? 'text-green-600 line-through' : isActive ? 'text-accent-navy' : 'text-text-muted'
              }`}>
                {p.label}
              </span>
              {isActive && phase === 'upload' && (
                <span className="ml-auto text-[10px] font-black text-accent-gold">{s3Progress}%</span>
              )}
            </div>
          );
        })}
      </div>

      {/* Inline S3 progress bar (upload phase only) */}
      {phase === 'upload' && (
        <div className="space-y-1.5">
          <div className="flex justify-between text-[10px] font-bold text-text-muted">
            <span>Direct S3 transfer</span>
            <span>{s3Progress}%</span>
          </div>
          <div className="h-2 bg-brand-dark rounded-full overflow-hidden">
            <motion.div
              className="h-full bg-gradient-to-r from-accent-gold to-amber-500 rounded-full"
              animate={{ width: `${s3Progress}%` }}
              transition={{ ease: 'linear', duration: 0.2 }}
            />
          </div>
        </div>
      )}
    </motion.div>
  );
}

// ── ErrorBanner ───────────────────────────────────────────────────────────────
function ErrorBanner({ message }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }}
      className="flex items-start gap-2 p-3 bg-red-50 border border-red-100 rounded-xl text-xs text-red-700"
    >
      <SafeIcon icon={FiAlertTriangle} className="w-4 h-4 flex-shrink-0 mt-0.5" />
      <span>{message}</span>
    </motion.div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────
const DocuVaultUploadModal = ({ isOpen, onClose, onSuccess }) => {
  const fileRef  = useRef();
  const dropRef  = useRef();

  const [file,       setFile]       = useState(null);
  const [step,       setStep]       = useState(1);
  const [uploading,  setUploading]  = useState(false);
  const [done,       setDone]       = useState(false);
  const [error,      setError]      = useState('');
  const [phase,      setPhase]      = useState('checksum');
  const [s3Progress, setS3Progress] = useState(0);
  const [apiStatus,  setApiStatus]  = useState('unknown');
  const [dragging,   setDragging]   = useState(false);
  const [meta,       setMeta]       = useState(defaultMeta);
  const [newParty,   setNewParty]   = useState({ name: '', email: '', role: 'executor' });

  useEffect(() => {
    if (!isOpen) return;
    checkApiHealth().then(h => setApiStatus(h.status === 'ok' ? 'ok' : 'offline'));
  }, [isOpen]);

  const reset = useCallback(() => {
    setFile(null); setStep(1); setUploading(false); setDone(false);
    setError(''); setPhase('checksum'); setS3Progress(0);
    setMeta(defaultMeta);
    setNewParty({ name: '', email: '', role: 'executor' });
  }, []);

  const handleClose = () => { reset(); onClose(); };

  // ── File validation ─────────────────────────────────────────────────────────
  const validateAndSetFile = useCallback((f) => {
    if (!f) return;
    if (f.size > MAX_MB * 1024 * 1024) {
      setError(`File exceeds the ${MAX_MB} MB limit (${formatBytes(f.size)}).`);
      return;
    }
    const ext = f.name.split('.').pop()?.toLowerCase();
    const allowed = ['pdf','doc','docx','jpg','jpeg','png','txt','xls','xlsx'];
    if (!allowed.includes(ext)) {
      setError(`".${ext}" is not supported. Allowed: ${allowed.join(', ')}.`);
      return;
    }
    setFile(f); setError('');
  }, []);

  const handleFileInput = (e) => validateAndSetFile(e.target.files?.[0]);
  const onDragOver      = (e) => { e.preventDefault(); setDragging(true);  };
  const onDragLeave     = (e) => { e.preventDefault(); setDragging(false); };
  const onDrop          = (e) => {
    e.preventDefault(); setDragging(false);
    validateAndSetFile(e.dataTransfer.files?.[0]);
  };

  // ── Authorized parties ──────────────────────────────────────────────────────
  const addParty = () => {
    if (!newParty.name.trim() || !newParty.email.trim()) return;
    setMeta(m => ({
      ...m,
      authorizedParties: [...m.authorizedParties, { ...newParty, id: crypto.randomUUID() }],
    }));
    setNewParty({ name: '', email: '', role: 'executor' });
  };
  const removeParty = (id) => setMeta(m => ({ ...m, authorizedParties: m.authorizedParties.filter(p => p.id !== id) }));

  // ── Upload pipeline ─────────────────────────────────────────────────────────
  const handleUpload = async () => {
    if (!file) return;
    setUploading(true); setError(''); setPhase('checksum'); setS3Progress(0);

    try {
      await uploadDocument(
        file,
        { ...meta, tags: meta.tags.split(',').map(t => t.trim()).filter(Boolean) },
        (pct, currentPhase) => {
          if (currentPhase) setPhase(currentPhase);
          if (typeof pct === 'number') setS3Progress(pct);
        }
      );

      setPhase('done');
      setDone(true);
      setTimeout(() => { onSuccess?.(); handleClose(); }, 2200);
    } catch (e) {
      const msg = e.message || 'Upload failed. Please try again.';
      setError(
        msg.includes('sign in')
          ? 'Session expired. Please sign in again.'
          : msg.includes('presign') || msg.includes('URL')
            ? `Server error: Could not get a secure upload URL. Is the S3 API running? (${msg})`
            : msg.includes('S3 upload failed')
              ? `Transfer error: The file could not be sent to S3. (${msg})`
              : msg
      );
    } finally {
      setUploading(false);
    }
  };

  if (!isOpen) return null;

  const steps = ['Select File', 'Document Info', 'Access Control'];

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-accent-navy/70 backdrop-blur-md">
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 20 }} animate={{ opacity: 1, scale: 1, y: 0 }}
        className="bg-white rounded-[40px] shadow-2xl w-full max-w-2xl overflow-hidden border border-brand-dark max-h-[92vh] flex flex-col"
      >
        {/* Header */}
        <div className="p-7 border-b border-brand-light flex items-center justify-between bg-gradient-to-r from-accent-navy to-[#1a2a5e] flex-shrink-0">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-accent-gold text-accent-navy rounded-2xl flex items-center justify-center shadow-lg">
              <SafeIcon icon={FiUploadCloud} className="w-6 h-6" />
            </div>
            <div>
              <h3 className="text-xl font-serif text-white">Secure Document Upload</h3>
              <p className="text-white/50 text-[10px] font-black uppercase tracking-widest">
                AES-256 Encrypted · AWS S3 · Pre-Signed Transfer
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[9px] font-black uppercase tracking-widest border ${
              apiStatus === 'ok'      ? 'bg-green-500/10 border-green-500/30 text-green-300' :
              apiStatus === 'offline' ? 'bg-red-500/10 border-red-500/30 text-red-300'       :
                                        'bg-white/5 border-white/10 text-white/40'
            }`}>
              <div className={`w-1.5 h-1.5 rounded-full ${
                apiStatus === 'ok' ? 'bg-green-400 animate-pulse' :
                apiStatus === 'offline' ? 'bg-red-400' : 'bg-white/30'
              }`} />
              {apiStatus === 'ok' ? 'API Online' : apiStatus === 'offline' ? 'API Offline' : 'Checking…'}
            </div>
            <button onClick={handleClose} className="p-2 text-white/60 hover:text-white rounded-xl transition-colors">
              <SafeIcon icon={FiX} className="w-5 h-5" />
            </button>
          </div>
        </div>

        <ApiStatusBanner status={apiStatus} />

        {/* Step indicator */}
        {!uploading && !done && (
          <div className="flex items-center gap-0 px-8 py-4 border-b border-brand-light bg-brand-light/50 flex-shrink-0">
            {steps.map((s, i) => (
              <React.Fragment key={s}>
                <div className="flex items-center gap-2">
                  <div className={`w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-black transition-all ${
                    step > i + 1 ? 'bg-green-500 text-white' : step === i + 1 ? 'bg-accent-navy text-white' : 'bg-brand-dark text-text-muted'
                  }`}>
                    {step > i + 1 ? <SafeIcon icon={FiCheck} className="w-3.5 h-3.5" /> : i + 1}
                  </div>
                  <span className={`text-[10px] font-black uppercase tracking-widest ${step === i + 1 ? 'text-accent-navy' : 'text-text-muted'}`}>{s}</span>
                </div>
                {i < steps.length - 1 && <div className="flex-1 h-0.5 bg-brand-dark mx-3" />}
              </React.Fragment>
            ))}
          </div>
        )}

        {/* Body */}
        <div className="overflow-y-auto flex-1 p-8">
          <AnimatePresence mode="wait">

            {/* Uploading / Done */}
            {(uploading || done) && (
              <motion.div key="uploading" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                <UploadProgressPanel phase={phase} s3Progress={s3Progress} />
                {error && (
                  <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
                    className="mt-6 p-4 bg-red-50 border border-red-200 rounded-2xl">
                    <div className="flex items-start gap-3">
                      <SafeIcon icon={FiAlertTriangle} className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                      <div className="flex-1">
                        <p className="text-xs font-bold text-red-700 mb-1">Upload Failed</p>
                        <p className="text-[11px] text-red-600 leading-relaxed">{error}</p>
                      </div>
                    </div>
                    <button
                      onClick={() => { setUploading(false); setError(''); setPhase('checksum'); setS3Progress(0); }}
                      className="mt-3 flex items-center gap-1.5 text-[10px] font-black uppercase tracking-widest text-red-600 hover:text-red-800 transition-colors"
                    >
                      <SafeIcon icon={FiRefreshCw} className="w-3.5 h-3.5" /> Retry Upload
                    </button>
                  </motion.div>
                )}
              </motion.div>
            )}

            {/* Step 1: File Selection */}
            {!uploading && !done && step === 1 && (
              <motion.div key="s1" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} className="space-y-5">
                <div
                  ref={dropRef}
                  onDragOver={onDragOver} onDragLeave={onDragLeave} onDrop={onDrop}
                  onClick={() => !file && fileRef.current?.click()}
                  className={`relative block border-2 border-dashed rounded-3xl p-10 text-center transition-all ${
                    dragging
                      ? 'border-accent-gold bg-accent-gold/5 scale-[1.01]'
                      : file
                        ? 'border-green-400 bg-green-50 cursor-default'
                        : 'border-brand-dark hover:border-accent-gold bg-brand-light/30 cursor-pointer'
                  }`}
                >
                  <input ref={fileRef} type="file" className="hidden" onChange={handleFileInput} accept={ACCEPTED} />
                  {dragging && (
                    <div className="absolute inset-0 rounded-3xl flex items-center justify-center bg-accent-gold/10 pointer-events-none">
                      <p className="text-accent-gold font-black text-sm uppercase tracking-widest">Drop to select</p>
                    </div>
                  )}
                  {file ? (
                    <div>
                      <div className="w-16 h-16 bg-green-100 text-green-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
                        <SafeIcon icon={FiFile} className="w-8 h-8" />
                      </div>
                      <p className="text-base font-bold text-green-700 break-all">{file.name}</p>
                      <p className="text-xs text-green-600 mt-1">{formatBytes(file.size)} · {file.type || 'Unknown type'}</p>
                      <button
                        onClick={(e) => { e.stopPropagation(); setFile(null); setError(''); }}
                        className="mt-3 text-[10px] font-black uppercase tracking-widest text-red-400 hover:text-red-600 transition-colors"
                      >
                        Remove & Choose Different File
                      </button>
                    </div>
                  ) : (
                    <div>
                      <SafeIcon icon={FiUploadCloud} className="w-12 h-12 text-accent-gold mx-auto mb-4" />
                      <p className="text-base font-bold text-accent-navy mb-1">Drop your document here</p>
                      <p className="text-xs text-text-muted mb-1">or click to browse</p>
                      <p className="text-[10px] text-text-muted/70">PDF, DOCX, XLSX, JPG, PNG · Max {MAX_MB} MB</p>
                    </div>
                  )}
                </div>
                <div className="p-4 bg-accent-navy/5 border border-accent-navy/10 rounded-2xl flex items-start gap-3">
                  <SafeIcon icon={FiShield} className="w-5 h-5 text-accent-navy flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-xs font-bold text-accent-navy">Pre-Signed S3 Transfer</p>
                    <p className="text-[10px] text-text-muted font-light leading-relaxed">
                      Your file is sent directly to AWS S3 via a time-limited pre-signed URL — your credentials
                      never leave our server. AES-256 encryption is applied at rest.
                    </p>
                  </div>
                </div>
                {error && <ErrorBanner message={error} />}
              </motion.div>
            )}

            {/* Step 2: Document Metadata */}
            {!uploading && !done && step === 2 && (
              <motion.div key="s2" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} className="space-y-5">
                <div>
                  <label className="text-[10px] font-black uppercase tracking-widest text-accent-navy block mb-2">Document Category</label>
                  <div className="grid grid-cols-3 gap-2">
                    {DOCUMENT_CATEGORIES.filter(c => c.id !== 'all').map(cat => (
                      <button key={cat.id} onClick={() => setMeta(m => ({ ...m, category: cat.id }))}
                        className={`p-3 rounded-2xl border-2 text-left transition-all ${
                          meta.category === cat.id ? 'border-accent-gold bg-accent-gold/5' : 'border-brand-dark hover:border-accent-gold/50'
                        }`}>
                        <p className="text-[9px] font-black uppercase tracking-widest text-accent-navy">{cat.label}</p>
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="text-[10px] font-black uppercase tracking-widest text-accent-navy block mb-2">Description (Optional)</label>
                  <textarea value={meta.description} onChange={e => setMeta(m => ({ ...m, description: e.target.value }))}
                    rows={3} placeholder="Brief description of this document…"
                    className="w-full px-4 py-3 bg-brand-light border-2 border-brand-dark rounded-2xl outline-none focus:border-accent-gold text-sm resize-none" />
                </div>
                <div>
                  <label className="text-[10px] font-black uppercase tracking-widest text-accent-navy block mb-2">Tags (comma-separated)</label>
                  <input value={meta.tags} onChange={e => setMeta(m => ({ ...m, tags: e.target.value }))}
                    placeholder="estate, legal, 2024…"
                    className="w-full px-4 py-3 bg-brand-light border-2 border-brand-dark rounded-2xl outline-none focus:border-accent-gold text-sm" />
                </div>
                <div>
                  <label className="text-[10px] font-black uppercase tracking-widest text-accent-navy block mb-2">Document Expiry (Optional)</label>
                  <input type="date" value={meta.expiryDate}
                    onChange={e => setMeta(m => ({ ...m, expiryDate: e.target.value }))}
                    min={new Date().toISOString().split('T')[0]}
                    className="w-full px-4 py-3 bg-brand-light border-2 border-brand-dark rounded-2xl outline-none focus:border-accent-gold text-sm" />
                </div>
              </motion.div>
            )}

            {/* Step 3: Access Control */}
            {!uploading && !done && step === 3 && (
              <motion.div key="s3" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} className="space-y-5">
                <div>
                  <label className="text-[10px] font-black uppercase tracking-widest text-accent-navy block mb-2">Access Level</label>
                  <div className="space-y-2">
                    {ACCESS_LEVELS.map(al => (
                      <button key={al.id} onClick={() => setMeta(m => ({ ...m, accessLevel: al.id }))}
                        className={`w-full p-4 rounded-2xl border-2 text-left transition-all ${
                          meta.accessLevel === al.id ? 'border-accent-gold bg-accent-gold/5' : 'border-brand-dark hover:border-accent-gold/50'
                        }`}>
                        <p className="text-xs font-bold text-accent-navy">{al.label}</p>
                        <p className="text-[10px] text-text-muted font-light mt-0.5">{al.desc}</p>
                      </button>
                    ))}
                  </div>
                </div>

                {meta.accessLevel !== 'private' && (
                  <div>
                    <label className="text-[10px] font-black uppercase tracking-widest text-accent-navy block mb-2">Authorized Parties</label>
                    <div className="space-y-2 mb-3">
                      {meta.authorizedParties.map(p => (
                        <div key={p.id} className="flex items-center justify-between p-3 bg-brand-light rounded-xl border border-brand-dark">
                          <div>
                            <p className="text-xs font-bold text-accent-navy">{p.name}</p>
                            <p className="text-[10px] text-text-muted">{p.email} · {p.role}</p>
                          </div>
                          <button onClick={() => removeParty(p.id)} className="text-red-400 hover:text-red-600 transition-colors">
                            <SafeIcon icon={FiTrash2} className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                    <div className="grid grid-cols-2 gap-2 mb-2">
                      <input value={newParty.name} onChange={e => setNewParty(n => ({ ...n, name: e.target.value }))}
                        placeholder="Full Name"
                        className="px-3 py-2.5 bg-brand-light border-2 border-brand-dark rounded-xl outline-none focus:border-accent-gold text-xs" />
                      <input value={newParty.email} onChange={e => setNewParty(n => ({ ...n, email: e.target.value }))}
                        placeholder="Email Address" type="email"
                        className="px-3 py-2.5 bg-brand-light border-2 border-brand-dark rounded-xl outline-none focus:border-accent-gold text-xs" />
                    </div>
                    <div className="flex gap-2">
                      <select value={newParty.role} onChange={e => setNewParty(n => ({ ...n, role: e.target.value }))}
                        className="flex-1 px-3 py-2.5 bg-brand-light border-2 border-brand-dark rounded-xl outline-none focus:border-accent-gold text-xs">
                        {['executor','beneficiary','attorney','trustee','co-trustee'].map(r => (
                          <option key={r} value={r}>{r.charAt(0).toUpperCase() + r.slice(1)}</option>
                        ))}
                      </select>
                      <button onClick={addParty}
                        className="px-4 py-2.5 bg-accent-navy text-white rounded-xl text-xs font-black flex items-center gap-1 hover:bg-accent-navy/90 transition-all">
                        <SafeIcon icon={FiPlus} className="w-3.5 h-3.5" /> Add
                      </button>
                    </div>
                  </div>
                )}

                {meta.accessLevel === 'multi_party' && (
                  <div className="p-4 bg-purple-50 border border-purple-100 rounded-2xl">
                    <label className="text-[10px] font-black uppercase tracking-widest text-purple-800 block mb-2">
                      Required Approvals (of {meta.authorizedParties.length} parties)
                    </label>
                    <input type="number" min={1} max={Math.max(meta.authorizedParties.length, 1)}
                      value={meta.authorizationThreshold}
                      onChange={e => setMeta(m => ({ ...m, authorizationThreshold: parseInt(e.target.value) }))}
                      className="w-full px-4 py-3 bg-white border-2 border-purple-200 rounded-xl outline-none focus:border-purple-400 text-sm" />
                    <p className="text-[10px] text-purple-600 mt-1.5 font-light">
                      Document released when {meta.authorizationThreshold} of {meta.authorizedParties.length} parties approve.
                    </p>
                  </div>
                )}

                {error && <ErrorBanner message={error} />}
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Footer */}
        {!uploading && !done && (
          <div className="p-6 border-t border-brand-light flex items-center justify-between flex-shrink-0 bg-brand-light/30">
            <button
              onClick={step === 1 ? handleClose : () => setStep(s => s - 1)}
              className="px-6 py-3 border-2 border-brand-dark text-accent-navy rounded-2xl text-[10px] font-black uppercase tracking-widest hover:border-accent-navy transition-all"
            >
              {step === 1 ? 'Cancel' : 'Back'}
            </button>
            {step < 3 ? (
              <button
                onClick={() => {
                  if (step === 1 && !file) { setError('Please select a file first.'); return; }
                  setError(''); setStep(s => s + 1);
                }}
                className="px-8 py-3 bg-accent-navy text-white rounded-2xl text-[10px] font-black uppercase tracking-widest hover:bg-accent-navy/90 transition-all"
              >
                Continue →
              </button>
            ) : (
              <button
                onClick={handleUpload}
                disabled={apiStatus === 'offline'}
                className={`flex items-center gap-2 px-8 py-3 rounded-2xl text-[10px] font-black uppercase tracking-widest transition-all shadow-lg ${
                  apiStatus === 'offline'
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    : 'bg-accent-gold text-white hover:bg-accent-gold/90 shadow-accent-gold/20'
                }`}
              >
                <SafeIcon icon={FiShield} className="w-4 h-4" />
                {apiStatus === 'offline' ? 'API Offline' : 'Upload to DocuVault'}
              </button>
            )}
          </div>
        )}
      </motion.div>
    </div>
  );
};

export default DocuVaultUploadModal;